/**
 * Incremental-add helpers for updateGanttData (utils.js).
 *
 * A refetch after a scope/structure import delivers the new rows flat and in
 * arbitrary order, and a child's parent may itself be part of the same batch.
 * A single "look the parent up in the store" pass therefore drops whole
 * imported hierarchies (the parent record doesn't exist yet at lookup time),
 * and downstream assignment wiring ends up referencing events that were never
 * inserted. Nest batch-internal children under their in-batch parent first,
 * then insert only the batch roots against the store — falling back to a
 * root-level add instead of dropping rows whose parent cannot be found.
 */

/**
 * The ordinals a payload carries more than once. Such an ordinal names NO row:
 * every ordinal-based resolution in the client has to skip it, because picking
 * whichever row a map happened to keep buries a whole subtree under an
 * arbitrary summary, silently and with no way back.
 */
export const ambiguousUxIds = (items) => {
  const seen = new Set();
  const ambiguous = new Set();
  items.forEach((item) => {
    if (item.uxId == null) return;
    if (seen.has(item.uxId)) ambiguous.add(item.uxId);
    else seen.add(item.uxId);
  });
  return ambiguous;
};

export const nestBatchChildren = (items) => {
  const byUxId = new Map();
  const byId = new Map();
  const ambiguous = ambiguousUxIds(items);
  items.forEach((item) => {
    if (item.id != null) byId.set(String(item.id), item);
    if (item.uxId == null || ambiguous.has(item.uxId)) return;
    byUxId.set(item.uxId, item);
  });

  const roots = [];
  items.forEach((item) => {
    const parentInBatch =
      item.parentSaId != null
        ? byId.get(String(item.parentSaId))
        : item.parentUxId && byUxId.get(item.parentUxId);
    if (parentInBatch && parentInBatch !== item) {
      (parentInBatch.children = parentInBatch.children || []).push(item);
    } else {
      roots.push(item);
    }
  });
  return roots;
};

/**
 * The parent travels as `parentSaId`, a row id, and that is what is resolved
 * here — see the backend's `docs/gantt-parenting-contract.md`.
 *
 * uxId is an ordinal sequence position (1..N), not an identity: the backend
 * renumbers it across the whole scenario whenever rows are added, moved or
 * reordered. It is a reliable key WITHIN one generation of the numbering, so
 * `ordinalGenerationOf` decides first what this payload still supports: resolved
 * against the payload — which yields the parent's stable `id` — only while the
 * payload is one generation, and against the store only while it is the store's.
 * Resolving across generations attached rows to whichever row happened to carry
 * the ordinal.
 *
 * A uxId that names two rows of the same payload resolves to neither: a
 * misresolved parent moves a whole subtree, and there is no way back from it.
 */
const buildPayloadIdIndex = (items) => {
  const ambiguous = ambiguousUxIds(items);
  const idByUxId = new Map();
  items.forEach((item) => {
    if (item.uxId == null || item.id == null || ambiguous.has(item.uxId)) return;
    idByUxId.set(item.uxId, String(item.id));
  });
  return { idByUxId, ambiguous };
};

const indexStoreById = (storedRecords) => {
  const byId = new Map();
  storedRecords.forEach((record) => byId.set(String(record.id), record));
  return byId;
};

/**
 * Which ordinal resolutions this payload still supports, judged against the store.
 *
 * The client routinely holds two generations of the numbering at once: the
 * realignment writes the server's fresh ordinals onto the Bryntum records, while
 * a Redux row keeps the one it was read with until something rewrites it — and a
 * gesture's response, or an edit's echo, only rewrites the rows it touched.
 *
 * The store is one generation by construction (a full load and the realignment
 * both write every row), so it is the reference. Every row the payload and the
 * store both hold votes: same ordinal, or not.
 *
 * - **All votes the same way** — the payload is one generation too, so its own
 *   ordinals still describe its own hierarchy and can be resolved within it.
 * - **Votes split** — the payload is a patchwork of generations, and an ordinal
 *   in it names a row from one generation and a parent from another. Nothing
 *   ordinal-based is usable; only `parentSaId` is.
 * - **No disagreement at all** — the payload is on the store's own generation, so
 *   the store's index is usable too, for a parent the payload does not carry.
 *
 * No overlap means no evidence either way (an insert-only batch), and both stay
 * available.
 */
export const ordinalGenerationOf = (byId, items) => {
  let agreeing = 0;
  let disagreeing = 0;
  items.forEach((item) => {
    if (item.uxId == null || item.id == null) return;
    const record = byId.get(String(item.id));
    if (!record || record.uxId == null) return;
    if (record.uxId === item.uxId) agreeing += 1;
    else disagreeing += 1;
  });
  return {
    withinPayload: agreeing === 0 || disagreeing === 0,
    againstStore: disagreeing === 0,
  };
};

const EVERY_ORDINAL = { withinPayload: true, againstStore: true };

const makeParentResolver = (items, byId, generation) => {
  const { idByUxId, ambiguous } = buildPayloadIdIndex(items);
  return (item) => {
    if (item.parentSaId != null) return byId.get(String(item.parentSaId)) || null;
    const { parentUxId } = item;
    if (parentUxId == null || ambiguous.has(parentUxId)) return null;
    if (!generation.withinPayload) return null;
    const parentId = idByUxId.get(parentUxId);
    if (parentId != null) return byId.get(parentId) || null;
    // The payload does not name this parent, so the only candidate left is the
    // store's own numbering — usable exactly when it is the same generation.
    return generation.againstStore
      ? byUxIdOf(byId).get(parentUxId) || null
      : null;
  };
};

// Built on demand: the store's ordinal index is only ever consulted by the
// fallback above, which most payloads never reach.
const byUxIdOf = (byId) => {
  let index = byUxIdCache.get(byId);
  if (index) return index;
  index = new Map();
  byId.forEach((record) => {
    if (record.uxId != null) index.set(record.uxId, record);
  });
  byUxIdCache.set(byId, index);
  return index;
};
const byUxIdCache = new WeakMap();

const hasParent = (item) => item.parentSaId != null || !!item.parentUxId;

export const insertTasksIntoStore = (eventStore, items, generation) => {
  // allRecords, not records/find: those only expose the VISIBLE subset, so with
  // a filter on, an existing row read as absent — the parent lookup fell back to
  // a root-level add, and an id already in the store was inserted a second time
  // ("Id collision", duplicated rows).
  const storedRecords = eventStore.allRecords;
  const byId = indexStoreById(storedRecords);
  const freshItems = items.filter((item) => !byId.has(String(item.id)));
  if (!freshItems.length) return;

  const resolveParent = makeParentResolver(
    items,
    byId,
    generation ?? EVERY_ORDINAL
  );
  // One appendChild per target parent instead of one per row: each call runs a
  // store refresh and an engine notification, and an import on a large planning
  // delivers thousands of rows under a handful of summaries.
  const byParent = new Map();
  const rootItems = [];
  nestBatchChildren(freshItems).forEach((item) => {
    const parentRecord = hasParent(item) ? resolveParent(item) : null;
    if (!parentRecord) {
      rootItems.push(item);
      return;
    }
    const group = byParent.get(parentRecord);
    if (group) group.push(item);
    else byParent.set(parentRecord, [item]);
  });

  eventStore.beginBatch();
  try {
    byParent.forEach((children, parentRecord) => {
      parentRecord.appendChild(children);
    });
    if (rootItems.length) eventStore.add(rootItems);
  } finally {
    eventStore.endBatch();
  }
};

const isWithinSubtree = (candidate, record) => {
  for (let node = candidate; node; node = node.parent) {
    if (node === record) return true;
  }
  return false;
};

/**
 * Re-apply the payload's parentage to the rows the store already holds.
 *
 * The update pass in updateGanttData writes scalars only — it never touches
 * `parentId`/`parentUxId` — so nothing in the refetch path ever moves a row.
 * Two consequences, both user-visible:
 *   - a re-parent decided on the backend (an activity moved under another
 *     summary) only reached the Gantt after a full reload;
 *   - the deletion branch outdents every surviving child of a deleted parent,
 *     and that outdent is one-way. A payload that turns out to have been wrong
 *     (a backend answering mid-regeneration right after an "update usage" job)
 *     leaves the tree permanently flat: the follow-up correct payload restores
 *     the rows but not the hierarchy.
 *
 * This pass makes the next correct payload self-healing. It only ever moves a
 * row to a parent the payload names AND the store holds: a row is never demoted
 * to the root, so a payload that omits parentage — or names a parent that is
 * filtered out of this dataset — cannot flatten anything.
 */
export const reconcileTaskParents = (eventStore, items, generation) => {
  const storedRecords = eventStore.allRecords;
  const byId = indexStoreById(storedRecords);
  const resolveParent = makeParentResolver(
    items,
    byId,
    generation ?? ordinalGenerationOf(byId, items)
  );

  const byParent = new Map();
  items.forEach((item) => {
    // No parent named means "root", and acting on it could only ever demote a row
    // the store nests correctly — the flattening this pass exists to repair.
    if (!hasParent(item)) return;
    const record = byId.get(String(item.id));
    if (!record) return;
    const parentRecord = resolveParent(item);
    if (!parentRecord || parentRecord === record) return;
    if (record.parent === parentRecord) return;
    // Appending an ancestor's row under its own descendant detaches the whole
    // branch from the root; the engine has no way back from it.
    if (isWithinSubtree(parentRecord, record)) return;
    const group = byParent.get(parentRecord);
    if (group) group.push(record);
    else byParent.set(parentRecord, [record]);
  });

  if (!byParent.size) return [];

  const reparented = [];
  // Grouped and batched for the same reason as the insert above: this pass runs
  // on every refetch, over the whole payload, and each ungrouped appendChild
  // costs a store refresh on a planning that can hold tens of thousands of rows.
  eventStore.beginBatch();
  try {
    byParent.forEach((records, parentRecord) => {
      // Re-checked against the live tree: an earlier group's move can have made
      // a pending one cyclic, which the up-front pass could not see.
      const movable = records.filter(
        (record) =>
          record.parent !== parentRecord && !isWithinSubtree(parentRecord, record)
      );
      if (!movable.length) return;
      parentRecord.appendChild(movable);
      reparented.push(...movable);
    });
  } finally {
    eventStore.endBatch();
  }

  return reparented;
};
